import React, { useState, useEffect, useRef } from 'react';
import { Typography, TextField, Button, Grid2, Card, CardContent, MenuItem, Box, InputAdornment, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Tooltip, Autocomplete, useTheme } from '@mui/material';
import { Add, Search, Edit, Delete, Person, Email, Phone, Home, Group, Category, Notes, InfoOutlined } from '@mui/icons-material';
import { getEventGuests, getUserGuests, createGuest, updateGuest, deleteGuest, exportContacts, importContacts } from '../actions/guests';
import { useNavigate, useParams } from 'react-router-dom';
import AccessDenied from '../components/AccessDenied';
import { useAlert } from '../contexts/AlertContext';
import { parseContactsCsv } from '../utils/contactsCsv';
import { cardGridSx, cardItemSx } from '../utils/cardLayout';
import GuestInvitationDialog from '../components/GuestInvitationDialog';
import GuestInvitationButton from '../components/GuestInvitationButton';

const GuestListManagement = () => {
  const theme = useTheme();
  const { showAlert } = useAlert();
  const { eventId } = useParams(); // Get eventId from URL params
  const [guests, setGuests] = useState([]);
  const [invitationGuest, setInvitationGuest] = useState(null);
  const [newGuest, setNewGuest] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    rsvp: 'Pending',
    plus_one: 0,
    category: '',
    notes: '',
    existingGuestId: null
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterRSVP, setFilterRSVP] = useState('');
  const [openGuestForm, setOpenGuestForm] = useState(false);
  const [openAddressBook, setOpenAddressBook] = useState(false);
  const [guestEditingIndex, setGuestEditingIndex] = useState(null); // Track if we are editing an existing guest
  const [guestEventEditingIndex, setGuestEventEditingIndex] = useState(null); // Track if we are editing an existing guest
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [guestToDelete, setGuestToDelete] = useState(null); // Track which guest is being deleted
  const [guestEventToDelete, setGuestEventToDelete] = useState(null); // Track which guest is being deleted
  const [addressBookGuests, setAddressBookGuests] = useState([]); // State to store address book guests
  const [accessDenied, setAccessDenied] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [openContactsHelp, setOpenContactsHelp] = useState(false);
  const importInput = useRef(null);

  // Fetch guests from API on load
  useEffect(() => {
    let active = true;
    const fetchGuests = async () => {
      try {
        const fetchedGuests = await getEventGuests(eventId);
        if (active) setGuests(fetchedGuests);
      } catch (error) {
        if (!active) return;
        if (error?.response?.status === 403) {
          setAccessDenied(true);
        } else {
          showAlert(error?.response?.data?.error || error?.response?.data?.message || error.message || 'Failed to load data', 'error');
        }
      }
    };
    fetchGuests();
    window.addEventListener('focus', fetchGuests);
    return () => { active = false; window.removeEventListener('focus', fetchGuests); };
  }, [eventId]);

  // Fetch address book guests
  const fetchAddressBook = async () => {
    try {
      const fetchedAddressBook = await getUserGuests();
      setAddressBookGuests(fetchedAddressBook);
    } catch (error) {
      if (error?.response?.status === 403) {
        setAccessDenied(true);
      } else {
        showAlert(error?.response?.data?.error || error?.response?.data?.message || error.message || 'Failed to load data', 'error');
      }
    }
  };

  // Handle input changes for the guest form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Convert plus_one to integer
    if (name === 'plus_one') {
      const intValue = value === '' ? 0 : parseInt(value, 10) || 0;
      setNewGuest({ ...newGuest, [name]: intValue });
    } else {
      setNewGuest({ ...newGuest, [name]: value });
    }
  };

  // Function to add or edit a guest
  const addGuest = async () => {
    try {
      // Ensure plus_one is an integer
      const guestData = {
        ...newGuest,
        plus_one: parseInt(newGuest.plus_one, 10) || 0
      };
      
      let response;
      if (guestEditingIndex !== null) {
        // Update guest
        response = await updateGuest(guestEditingIndex, guestEventEditingIndex, guestData);
        showAlert('Guest updated successfully!', 'success');
      } else {
        // Add new guest
        response = await createGuest(eventId, guestData);
        // Show server message if available
        if (response.message) {
          showAlert(response.message, 'success');
        } else {
          showAlert('Guest added successfully!', 'success');
        }
      }
      // Refresh guest list
      const updatedGuests = await getEventGuests(eventId);
      setGuests(updatedGuests);

      handleCloseGuestForm(); // Close and reset form
    } catch (error) {
      // Try to show server error message if available
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error.message ||
        'Failed to save guest';
      showAlert(errorMessage, 'error');
      console.error('Failed to save guest', error);
    }
  };

  // Open form for adding/editing guests
  const handleOpenGuestForm = () => setOpenGuestForm(true);

  // Open form for adding guests from address book
  const handleOpenAddressBook = () => {
    setOpenAddressBook(true);
    fetchAddressBook();
  };

  const handleExportContacts = async () => {
    setExporting(true);
    try {
      const file = await exportContacts();
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'event-planner-contacts.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      showAlert(error?.response?.data?.error || error.message || 'Failed to export contacts', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleImportContacts = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setImporting(true);
    try {
      if (file.size > 50000) throw new Error('CSV file must be 50 KB or smaller');
      const contacts = parseContactsCsv(await file.text());
      const { imported, skipped } = await importContacts(contacts);
      showAlert(`Imported ${imported} contact${imported === 1 ? '' : 's'}; skipped ${skipped} duplicate${skipped === 1 ? '' : 's'}.`, 'success');
      if (openAddressBook) await fetchAddressBook();
    } catch (error) {
      showAlert(error?.response?.data?.error || error.message || 'Failed to import contacts', 'error');
    } finally {
      setImporting(false);
    }
  };

  // Close form and reset newGuest data
  const handleCloseGuestForm = () => {
    setOpenGuestForm(false);
    setNewGuest({
      name: '',
      email: '',
      phone: '',
      address: '',
      rsvp: 'Pending',
      plus_one: 0,
      category: '',
      notes: '',
      id: null,
      guest_event_id: null,
      existingGuestId: null
    });
    setGuestEditingIndex(null);
    setGuestEventEditingIndex(null);
  };

  // Edit guest by populating form with selected guest's data
  const handleEdit = (index, guest_id, guest_event_id) => {
    setNewGuest({ ...guests[index] });
    setGuestEditingIndex(guest_id);
    setGuestEventEditingIndex(guest_event_id);
    setOpenGuestForm(true);
  };

  // Open delete confirmation dialog
  const handleDeleteOpen = (guest_id, guest_event_id) => {
    setGuestToDelete(guest_id);
    setGuestEventToDelete(guest_event_id);
    setOpenDeleteDialog(true);
  };

  // Confirm deletion of guest and refresh guest list
  const handleDeleteConfirm = async (deletionAction) => {
    try {
      // Call deleteGuest with the action
      await deleteGuest(guestToDelete, guestEventToDelete, deletionAction);
      setGuestToDelete(null);
      setGuestEventToDelete(null);
      const updatedGuests = await getEventGuests(eventId);  // Refresh guest list
      setGuests(updatedGuests);
      setOpenDeleteDialog(false); // Close delete dialog
    } catch (error) {
      showAlert(error?.response?.data?.error || error?.response?.data?.message || error.message || 'Operation failed', 'error');
      console.error('Failed to delete guest', error);
      alert('Failed to delete guest'); // Optional: show an alert for error handling
    }
  };

  // Handle selecting "delete completely" action
  const handleDeleteCompletely = () => {
    handleDeleteConfirm('delete_completely');
  };

  // Handle selecting "delete from event" action
  const handleDeleteFromEvent = () => {
    handleDeleteConfirm('delete_from_event');
  };

  // Cancel delete action
  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setGuestToDelete(null);
  };

  // Handle selecting a guest from address book
  const handleSelectFromAddressBook = (guest) => {
    setNewGuest({
      name: guest.name,
      email: guest.email,
      phone: guest.phone,
      address: guest.address,
      rsvp: 'Pending',
      plus_one: 0,
      category: '',
      notes: '',
      existingGuestId: guest.id
    });
    setOpenAddressBook(false); // Close address book dialog
    setOpenGuestForm(true); // Open the guest form to fill in additional details
  };

  // Filter guests based on search query, category, and RSVP status
  const filteredGuests = guests.filter((guest) => {
    return (
      guest.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
      (filterCategory === '' || guest.category === filterCategory) &&
      (filterRSVP === '' || guest.rsvp === filterRSVP)
    );
  });

  const getRSVPColor = (rsvp) => {
    const colorMap = {
      'Attending': theme.palette.success.main,
      'Not Attending': theme.palette.error.main,
      'Pending': theme.palette.warning.main,
    };
    return colorMap[rsvp] || theme.palette.text.secondary;
  };

  const getCardBorderColor = (rsvp) => {
    const borderColorMap = {
      'Attending': '2px solid ' + theme.palette.success.main,
      'Not Attending': '2px solid ' + theme.palette.error.main,
      'Pending': '2px solid ' + theme.palette.warning.main,
    };
    return borderColorMap[rsvp] || (theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.1)'
      : '1px solid rgba(255, 255, 255, 0.2)');
  };

  if (accessDenied) return <AccessDenied />;

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Controls Section */}
      <Box sx={{ mb: 4, mt: { xs: 4, sm: 0 } }}>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={handleOpenGuestForm}
              fullWidth
              sx={{ 
                height: '56px',
                borderRadius: 2,
                fontSize: { xs: '0.9rem', sm: '1rem' },
                fontWeight: 600,
                '&:hover': {
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Add New Guest
            </Button>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Add />}
              onClick={handleOpenAddressBook}
              fullWidth
              sx={{ 
                height: '56px',
                borderRadius: 2,
                fontSize: { xs: '0.9rem', sm: '1rem' },
                fontWeight: 600,
                '&:hover': {
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Add From Address Book
            </Button>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Search Guest by Name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }
              }}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <TextField
              select
              label="Filter by Category"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            >
              <MenuItem value="">All Categories</MenuItem>
              <MenuItem value="Family">Family</MenuItem>
              <MenuItem value="Friends">Friends</MenuItem>
              <MenuItem value="VIP">VIP</MenuItem>
            </TextField>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <TextField
              select
              label="Filter by RSVP"
              value={filterRSVP}
              onChange={(e) => setFilterRSVP(e.target.value)}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            >
              <MenuItem value="">All RSVP</MenuItem>
              <MenuItem value="Attending">Attending</MenuItem>
              <MenuItem value="Not Attending">Not Attending</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
            </TextField>
          </Grid2>
        </Grid2>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          mt: 3,
          p: { xs: 1.5, sm: 2 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Address book</Typography>
            <Tooltip title="How to import and export contacts">
              <IconButton
                aria-label="Contact CSV help"
                size="small"
                color="primary"
                onClick={() => setOpenContactsHelp(true)}
              >
                <InfoOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => importInput.current?.click()}
              disabled={importing}
              sx={{ minWidth: 175, borderRadius: 1.5, fontWeight: 600 }}
            >
              {importing ? 'Importing...' : 'Import Contacts'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleExportContacts}
              disabled={exporting}
              sx={{ minWidth: 175, borderRadius: 1.5, fontWeight: 600 }}
            >
              {exporting ? 'Exporting...' : 'Export Contacts'}
            </Button>
          </Box>
          <input
            ref={importInput}
            type="file"
            accept=".csv,text/csv"
            aria-label="Choose contacts CSV file"
            hidden
            onChange={handleImportContacts}
          />
        </Box>
      </Box>

      <Dialog
        open={openContactsHelp}
        onClose={() => setOpenContactsHelp(false)}
        aria-labelledby="contacts-help-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="contacts-help-title">Import and export contacts</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Choose Import Contacts to upload a UTF-8 .csv file. Imported contacts are saved to your
            address book, not added to this event. Use Add From Address Book to add them to an event.
            Export Contacts downloads your entire address book as a CSV file.
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Sample CSV format</Typography>
          <Box component="pre" sx={{
            m: 0,
            p: 2,
            borderRadius: 1,
            bgcolor: 'action.hover',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
            fontSize: '0.8rem',
          }}>
            {'Name,Address,Email,Phone\nJane Doe,"12 Main St, Apt 2, Seattle, WA 98101",jane@example.com,555-1234'}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Name is required; Address, Email and Phone may be blank. Keep quotes around addresses
            containing commas or line breaks so they stay in one column. The header can be in any
            order. Files can contain up to 500 contacts and be at most 50 KB. Existing names, emails
            or phone numbers are skipped without changing them.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenContactsHelp(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Guest List */}
      <Box sx={cardGridSx()}>
        {filteredGuests.length === 0 ? (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Card sx={{ 
              p: { xs: 4, sm: 6 }, 
              textAlign: 'center',
              background: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                : '0 4px 20px rgba(0, 0, 0, 0.1)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(255, 255, 255, 0.1)'
                : '1px solid rgba(255, 255, 255, 0.2)',
            }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                gap: 2
              }}>
                <Person sx={{ 
                  fontSize: 64, 
                  color: theme.palette.primary.main,
                  opacity: 0.7
                }} />
                <Box>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 600, 
                    color: theme.palette.text.primary,
                    mb: 1,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' }
                  }}>
                    {(searchQuery || filterCategory || filterRSVP) ? 
                      'No guests found for the selected filters' : 
                      'No guests added yet'
                    }
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ 
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    lineHeight: 1.5
                  }}>
                    {(searchQuery || filterCategory || filterRSVP) ? 
                      'Try adjusting your filters to see more results.' : 
                      'Start building your guest list by clicking "Add New Guest" above.'
                    }
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Box>
        ) : filteredGuests.map((guest, index) => (
          <Box key={guest.guest_event_id ?? index} sx={cardItemSx()}>
            <Card sx={{
              height: '100%',
              borderRadius: 3,
              background: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                : '0 4px 20px rgba(0, 0, 0, 0.1)',
              border: getCardBorderColor(guest.rsvp),
              transition: 'all 0.3s ease',
              position: 'relative',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 8px 30px rgba(0, 0, 0, 0.3)'
                  : '0 8px 30px rgba(0, 0, 0, 0.15)',
              }
            }}>
              {/* Plus One Badge - Top Right */}
              {guest.plus_one > 0 && (
                <Box sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  borderRadius: '50%',
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.7rem',
                  fontWeight: 'bold',
                  zIndex: 1,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}>
                  +{guest.plus_one}
                </Box>
              )}

              <CardContent sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, minHeight: '1.5rem', overflowWrap: 'anywhere' }}>
                  {guest.name}
                </Typography>

                {/* Guest Details Section */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Email sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.primary.main }} />
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      fontWeight: 500,
                      minWidth: 0,
                      overflowWrap: 'anywhere'
                    }}>
                      {guest.email || 'No email'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Phone sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.primary.main }} />
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      fontWeight: 500,
                      minWidth: 0,
                      overflowWrap: 'anywhere'
                    }}>
                      {guest.phone || 'No phone'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Home sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.primary.main }} />
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      fontWeight: 500,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {guest.address || 'No address'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Category sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.primary.main }} />
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      fontWeight: 500,
                      minWidth: 0,
                      overflowWrap: 'anywhere'
                    }}>
                      {guest.category || 'None'}
                    </Typography>
                  </Box>
                </Box>

                {/* Notes Section - Fixed height */}
                <Box sx={{ mb: 2, minHeight: '2.5rem' }}>
                  {guest.notes && (
                    <Tooltip 
                      title={guest.notes} 
                      arrow 
                      placement="top"
                      sx={{ 
                        '& .MuiTooltip-tooltip': {
                          maxWidth: 300,
                          fontSize: '0.875rem',
                          lineHeight: 1.4
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', cursor: 'help' }}>
                        <Notes sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, mt: 0.2, color: theme.palette.primary.main }} />
                        <Typography variant="body2" color="text.secondary" sx={{ 
                          fontSize: { xs: '0.8rem', sm: '0.875rem' }, 
                          fontStyle: 'italic',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {guest.notes}
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}
                </Box>

                {/* Action buttons - Always at bottom */}
                <Box sx={{ 
                  mt: 'auto',
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  pt: 2,
                  borderTop: `1px solid ${theme.palette.divider}`
                }}>
                  {/* RSVP Status - Bottom Left */}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Person sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: getRSVPColor(guest.rsvp) }} />
                    <Typography variant="body2" sx={{ 
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      color: getRSVPColor(guest.rsvp),
                      fontWeight: 600
                    }}>
                      {guest.rsvp}
                    </Typography>
                  </Box>

                  {/* Action Buttons - Bottom Right */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <GuestInvitationButton guest={guest} onClick={() => setInvitationGuest(guest)} />
                    <Tooltip title="Edit Guest" arrow>
                      <IconButton 
                        size="small"
                        onClick={() => handleEdit(index, guest.id, guest.guest_event_id)}
                        sx={{ 
                          color: theme.palette.primary.main,
                          '&:hover': { backgroundColor: theme.palette.primary.main + '20' }
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Guest" arrow>
                      <IconButton 
                        size="small"
                        onClick={() => handleDeleteOpen(guest.id, guest.guest_event_id)}
                        sx={{ 
                          color: theme.palette.error.main,
                          '&:hover': { backgroundColor: theme.palette.error.main + '20' }
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      <GuestInvitationDialog guest={invitationGuest} onClose={() => setInvitationGuest(null)} onChanged={invitation => setGuests(items => items.map(guest =>
        guest.guest_event_id === invitationGuest?.guest_event_id ? { ...guest, invitation: invitation === null ? null : { ...guest.invitation, ...invitation } } : guest))} />
      {/* Add/Edit Guest Dialog */}
      <Dialog open={openGuestForm} onClose={handleCloseGuestForm} fullWidth maxWidth="sm">
        <DialogTitle sx={{ 
          fontWeight: 600,
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
        }}>
          {guestEditingIndex !== null ? 'Edit Guest' : 'Add New Guest'}
        </DialogTitle>
        <DialogContent sx={{ 
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.02)'
            : 'rgba(255, 255, 255, 0.9)',
          pt: 2
        }}>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, sm: 6, md: 6 }}>
              <TextField
                label="Guest Name"
                name="name"
                value={newGuest.name}
                onChange={handleInputChange}
                margin="normal"
                fullWidth
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6, md: 6 }}>
              <TextField
                label="Email"
                name="email"
                value={newGuest.email}
                onChange={handleInputChange}
                margin="normal"
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6, md: 6 }}>
              <TextField
                label="Phone"
                name="phone"
                value={newGuest.phone}
                onChange={handleInputChange}
                margin="normal"
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6, md: 6 }}>
              <TextField
                label="Address"
                name="address"
                value={newGuest.address}
                onChange={handleInputChange}
                margin="normal"
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                label="RSVP Status"
                name="rsvp"
                value={newGuest.rsvp}
                onChange={handleInputChange}
                margin="normal"
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              >
                <MenuItem value="Attending">Attending</MenuItem>
                <MenuItem value="Not Attending">Not Attending</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
              </TextField>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                label="Category"
                name="category"
                value={newGuest.category}
                onChange={handleInputChange}
                margin="normal"
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              >
                <MenuItem value="Family">Family</MenuItem>
                <MenuItem value="Friends">Friends</MenuItem>
                <MenuItem value="VIP">VIP</MenuItem>
              </TextField>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label="Plus Ones"
                name="plus_one"
                value={newGuest.plus_one}
                onChange={handleInputChange}
                margin="normal"
                fullWidth
                type="number"
                inputProps={{
                  min: 0,
                  step: 1
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
              <TextField
                label="Notes"
                name="notes"
                value={newGuest.notes}
                onChange={handleInputChange}
                margin="normal"
                fullWidth
                multiline
                rows={3}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid2>
          </Grid2>
        </DialogContent>
        <DialogActions sx={{ 
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
          p: { xs: 1, sm: 2 },
          gap: { xs: 1, sm: 0 },
          flexDirection: 'row',
          alignItems: 'center'
        }}>
          <Button 
            onClick={handleCloseGuestForm} 
            color="error"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={addGuest} 
            color="primary"
            variant="contained"
            sx={{ borderRadius: 2 }}
          >
            {guestEditingIndex !== null ? 'Save' : 'Add Guest'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Address Book Dialog with Autocomplete */}
      <Dialog open={openAddressBook} onClose={() => setOpenAddressBook(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ 
          fontWeight: 600,
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
        }}>
          Select Guest from Address Book
        </DialogTitle>
        <DialogContent sx={{ 
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.02)'
            : 'rgba(255, 255, 255, 0.9)',
          pt: 2
        }}>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
            Select a guest to add from your address book:
          </Typography>

          <Autocomplete
            options={addressBookGuests}
            getOptionLabel={(option) => option.name}
            onChange={(event, value) => handleSelectFromAddressBook(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search for a guest"
                fullWidth
                variant="outlined"
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disableClearable
          />
        </DialogContent>
        <DialogActions sx={{ 
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
          p: { xs: 1, sm: 2 },
          gap: { xs: 1, sm: 0 },
          flexDirection: 'row',
          alignItems: 'center'
        }}>
          <Button 
            onClick={() => setOpenAddressBook(false)} 
            color="error"
            sx={{ borderRadius: 2 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Deleting Guest */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="sm">
        <DialogTitle sx={{ 
          fontWeight: 600,
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
        }}>
          Confirm Deletion
        </DialogTitle>
        <DialogContent sx={{ 
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.02)'
            : 'rgba(255, 255, 255, 0.9)',
          pt: 2
        }}>
          <DialogContentText sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            Are you sure you want to delete this guest? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ 
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
          p: { xs: 1, sm: 2 },
          gap: { xs: 1, sm: 0 },
          flexDirection: 'row',
          alignItems: 'center'
        }}>
          <Button 
            onClick={() => handleDeleteCancel()} 
            color="primary"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteCompletely} 
            color="error"
            variant="contained"
            sx={{ borderRadius: 2 }}
          >
            Delete Completely
          </Button>
          <Button 
            onClick={handleDeleteFromEvent} 
            color="warning"
            variant="contained"
            sx={{ borderRadius: 2 }}
          >
            Delete From Event Only
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GuestListManagement;
